import React from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ErrorOutlined from '@mui/icons-material/ErrorOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import { Button } from '@/ui';

/**
 * Catches a render error in the subtree and shows a recoverable card instead of a blank page.
 *
 * "Try again" clears the error and re-renders in place, which is enough when the cause was
 * transient (a failed lazy chunk, a bad response). The technical detail stays collapsed so the
 * page reads as a handled state rather than a crash, but is one click away for a bug report.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, detailsOpen: false };
  }

  componentDidUpdate(prevProps) {
    if (this.props.key !== prevProps.key) {
      this.setState({ hasError: false, error: null, detailsOpen: false });
    }
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    console.error('ErrorBoundary caught an error', error, errorInfo);
  }

  handleRetry = () => this.setState({ hasError: false, error: null, detailsOpen: false });

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { detailsOpen, error } = this.state;
    const detail = error?.stack || String(error ?? '');

    return (
      <Card sx={{ maxWidth: 720, mx: 'auto', mt: 4 }}>
        <CardContent>
          <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-start' }}>
            <ErrorOutlined sx={{ fontSize: '2.5rem', color: 'error.main', flex: '0 0 auto' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant='h6' gutterBottom>
                {this.props.message || 'Something went wrong.'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                This part of the page failed to load. Trying again often clears it; if it keeps happening, send the
                details below along with what you were doing.
              </Typography>

              <Stack direction='row' spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                <Button color='primary' onClick={this.handleRetry} startIcon={<RefreshOutlined />}>
                  Try again
                </Button>
                <Button
                  color='secondary'
                  outline
                  onClick={() => this.setState((prev) => ({ detailsOpen: !prev.detailsOpen }))}
                  endIcon={detailsOpen ? <ExpandLess /> : <ExpandMore />}
                  aria-expanded={detailsOpen}
                >
                  {detailsOpen ? 'Hide details' : 'Show details'}
                </Button>
              </Stack>

              <Collapse in={detailsOpen}>
                <Alert severity='error' variant='outlined' sx={{ mt: 2 }}>
                  <AlertTitle>Technical detail</AlertTitle>
                  <Box
                    component='pre'
                    sx={{
                      m: 0,
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: 240,
                      overflow: 'auto',
                    }}
                  >
                    {detail}
                  </Box>
                </Alert>
              </Collapse>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default ErrorBoundary;
